import React, { useState, useEffect } from 'react';
import { useMediaQuery } from '@mantine/hooks';

// Sample power-ups for LSAT test
const powerUps = [
  { id: 1, title: "Logic Master", description: "Boost your logical reasoning skills for the next 24 hours" },
  { id: 2, title: "Time Warp", description: "Gain an extra 5 minutes on your next practice section" },
  { id: 3, title: "Pattern Sight", description: "Instantly identify game patterns in analytical reasoning" },
  { id: 4, title: "Argument Shield", description: "Easily spot logical fallacies in arguments" },
  { id: 5, title: "Reading Rush", description: "25% faster reading speed for your next practice" },
  { id: 6, title: "Focus Beam", description: "Eliminate distractions for your next study session" },
  { id: 7, title: "Memory Boost", description: "Enhanced recall for rule memorization" },
  { id: 8, title: "Confidence Surge", description: "Reduced test anxiety on your next practice test" },
  { id: 9, title: "Error Detector", description: "Automatically catch careless mistakes" },
  { id: 10, title: "Clarity Vision", description: "See complex relationships between answer choices" },
];

// Card component
const Card = ({ card, position, flipped, onClick, leaving, entering }) => {
  return (
    <div 
      className={`absolute transform transition-all duration-500 cursor-pointer`}
      style={{
        transform: `translate(${position.x}px, ${position.y}px) rotate(${position.rotate}deg)`,
        zIndex: flipped ? 50 : 10,
        opacity: leaving ? 0 : 1,
        transition: 'transform 0.8s, opacity 0.8s',
        top: '50%',
        left: '50%',
        marginLeft: '-80px',
        marginTop: '-120px',
      }}
      onClick={!flipped ? onClick : undefined}
    >
      <div className="relative w-40 h-60 md:w-64 md:h-64">
        {/* Card front */}
        <div 
          className={`absolute inset-0 w-full h-full rounded-xl shadow-lg bg-gradient-to-br from-blue-600 to-purple-700 flex items-center justify-center transition-all duration-700 ${flipped ? 'opacity-0' : 'opacity-100'}`}
        >
          <div className="text-white font-bold text-xl md:text-2xl text-center p-4">LSAT Gain</div>
          <div className="absolute inset-3 border-2 border-white border-opacity-20 rounded-lg"></div>
        </div>
        
        {/* Card back */}
        <div 
          className={`absolute inset-0 w-full h-full rounded-xl shadow-lg bg-white p-4 md:p-6 flex flex-col justify-between transition-all duration-700 ${flipped ? 'opacity-100' : 'opacity-0'}`}
        >
          <div>
            <h3 className="text-lg md:text-2xl font-bold text-purple-700 mb-2">{card.title}</h3>
            <div className="w-12 h-1 bg-yellow-400 mb-2 md:mb-4"></div>
            <p className="text-sm md:text-base text-gray-700">{card.description}</p>
          </div>
          {/* <div className="text-xs md:text-sm text-gray-500 bg-gray-100 p-2 rounded text-center mt-2 md:mt-4">
            Power-up activated!
          </div> */}
        </div>
      </div>
    </div>
  );
};

// Magic effect component
const MagicEffect = () => {
  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
      {/* Central glow */}
      <div className="absolute w-16 h-16 bg-white rounded-full animate-ping opacity-70"></div>
      <div className="absolute w-24 h-24 border-4 border-yellow-300 rounded-full animate-ping opacity-60 delay-75"></div>
      <div className="absolute w-32 h-32 border-2 border-blue-400 rounded-full animate-ping opacity-50 delay-150"></div>
      
      {/* Sparkles */}
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i / 12) * Math.PI * 2;
        const distance = 80 + Math.random() * 60;
        const size = 3 + Math.random() * 6;
        const delay = i * 50;
        
        return (
          <div 
            key={i}
            style={{
              position: 'absolute',
              width: `${size}px`,
              height: `${size}px`,
              top: `calc(50% + ${Math.sin(angle) * distance}px)`,
              left: `calc(50% + ${Math.cos(angle) * distance}px)`,
              background: i % 2 ? '#fde047' : '#ffffff',
              borderRadius: '50%',
              animation: 'sparkle 1.5s forwards',
              animationDelay: `${delay}ms`,
              opacity: 0,
            }}
          />
        );
      })}
    </div>
  );
};

// Main component
const HeroBanner = () => {
  const isMobile = useMediaQuery(`(max-width : 760px)`);
  const [activeCards, setActiveCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);
  const [showMagic, setShowMagic] = useState(false);

  // Generate random positions for 3 cards
  const generateCardPositions = () => {
    return [
      { x: (isMobile ? 0 : -100)+-140, y: (isMobile ? -30 : -30)+50 + Math.random() * 20, rotate: -8 - Math.random() * 7 },
      { x: 0, y: (isMobile ? 0 : -100)+-20 + Math.random() * 40, rotate: -3 + Math.random() * 6 },
      { x: (isMobile ? 0 : 100)+140, y: (isMobile ? -10 : -10)+40 + Math.random() * 20, rotate: 8 + Math.random() * 7 },
    ];
  };

  // Handle card flip
  const flipCard = (id) => {
    if (!flippedCards[id]) {
      setFlippedCards(prev => ({ ...prev, [id]: true }));
    }
  };

  // Deal new cards
  const dealCards = () => {
    if (activeCards.length > 0) {
      // Discard current cards with animation
      setIsAnimating(true);
      
      setTimeout(() => {
        setActiveCards([]);
        setFlippedCards({});
        
        // Magic effect
        setShowMagic(true);
        
        setTimeout(() => {
          // Deal new cards
          const positions = generateCardPositions();
          const newCards = [];
          const usedIds = new Set();
          
          for (let i = 0; i < 3; i++) {
            let cardId;
            do {
              cardId = Math.floor(Math.random() * powerUps.length);
            } while (usedIds.has(cardId));
            
            usedIds.add(cardId);
            newCards.push({
              ...powerUps[cardId],
              position: positions[i],
              entering: true
            });
          }
          
          setActiveCards(newCards);
          setShowMagic(false);
          setIsAnimating(false);
        }, 1500);
      }, 600);
    } else {
      // First time dealing cards
      setShowMagic(true);
      
      setTimeout(() => {
        const positions = generateCardPositions();
        const newCards = [];
        const usedIds = new Set();
        
        for (let i = 0; i < 3; i++) {
          let cardId;
          do {
            cardId = Math.floor(Math.random() * powerUps.length);
          } while (usedIds.has(cardId));
          
          usedIds.add(cardId);
          newCards.push({
            ...powerUps[cardId],
            position: positions[i],
            entering: true
          });
        }
        
        setActiveCards(newCards);
        setShowMagic(false);
      }, 1500);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700">
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Floating particles */}
        {Array.from({ length: 20 }).map((_, i) => (
          <div 
            key={i}
            className="absolute rounded-full bg-white opacity-20"
            style={{
              width: `${2 + Math.random() * 6}px`,
              height: `${2 + Math.random() * 6}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
          />
        ))}
        
        {/* Glowing orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-yellow-300 opacity-20 blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 rounded-full bg-blue-400 opacity-20 blur-3xl"></div>
        <div className="absolute top-3/4 left-2/3 w-48 h-48 rounded-full bg-pink-400 opacity-20 blur-3xl"></div>
      </div>
      
      {/* Hero content */}
      <div className="relative z-10 container mx-auto px-4 py-16 md:py-24 flex flex-col items-center">
        <h1 className="text-3xl md:text-5xl font-bold text-white text-center mb-4">
          Pull three random cards per LSAT Gain &trade; deck
        </h1>
        <p className="text-lg md:text-xl text-white text-center mb-8 md:mb-12 max-w-2xl">
          Flip a card to reveal a unique power-up to help you ace the LSAT test
        </p>
        
        {/* Cards container */}
        <div className="relative w-full h-64 md:h-96 my-8 md:my-12">
          {showMagic && <MagicEffect />}
          
          {activeCards.map(card => (
            <Card 
              key={card.id}
              card={card}
              position={card.position}
              flipped={flippedCards[card.id]}
              onClick={() => flipCard(card.id)}
              leaving={isAnimating}
              entering={card.entering}
            />
          ))}
        </div>
        
        {/* Try Your Luck button */}
        <button
          onClick={dealCards}
          disabled={isAnimating}
          className="px-6 py-3 md:px-8 md:py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-bold text-lg md:text-xl rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50"
        >
          Try Your Luck
        </button>
      </div>
      
      {/* Custom animation styles */}
      <style jsx="true">{`
        @keyframes sparkle {
          0% { transform: scale(0); opacity: 1; }
          50% { transform: scale(1.5); opacity: 0.8; }
          100% { transform: scale(0); opacity: 0; }
        }
        
        .delay-75 {
          animation-delay: 75ms;
        }
        
        .delay-150 {
          animation-delay: 150ms;
        }
      `}</style>
    </div>
  );
};

export default HeroBanner;
